import RBush from "rbush";

export interface Point { x: number; y: number }
export interface Viewport { x: number; y: number; scale: number }
export interface Bounds { minX: number; minY: number; maxX: number; maxY: number }
export interface SpatialItem extends Bounds { id: string }
export interface CanvasRect { x: number; y: number; width: number; height: number; rotation?: number }

export const MIN_SCALE = 0.12;
export const MAX_SCALE = 4;

export function clampScale(scale: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

export function worldToScreen(point: Point, viewport: Viewport): Point {
  return { x: point.x * viewport.scale + viewport.x, y: point.y * viewport.scale + viewport.y };
}

export function screenToWorld(point: Point, viewport: Viewport): Point {
  return { x: (point.x - viewport.x) / viewport.scale, y: (point.y - viewport.y) / viewport.scale };
}

export function zoomAt(viewport: Viewport, anchor: Point, scale: number): Viewport {
  const nextScale = clampScale(scale);
  const worldAnchor = screenToWorld(anchor, viewport);
  return {
    scale: nextScale,
    x: anchor.x - worldAnchor.x * nextScale,
    y: anchor.y - worldAnchor.y * nextScale
  };
}

export function panBy(viewport: Viewport, delta: Point): Viewport {
  return { ...viewport, x: viewport.x + delta.x, y: viewport.y + delta.y };
}

export function viewportWorldBounds(viewport: Viewport, width: number, height: number, overscan = 200): Bounds {
  const topLeft = screenToWorld({ x: -overscan, y: -overscan }, viewport);
  const bottomRight = screenToWorld({ x: width + overscan, y: height + overscan }, viewport);
  return { minX: topLeft.x, minY: topLeft.y, maxX: bottomRight.x, maxY: bottomRight.y };
}

export function fitBounds(bounds: Bounds, viewportWidth: number, viewportHeight: number, padding = 72): Viewport {
  const contentWidth = Math.max(1, bounds.maxX - bounds.minX);
  const contentHeight = Math.max(1, bounds.maxY - bounds.minY);
  const scale = clampScale(Math.min((viewportWidth - padding * 2) / contentWidth, (viewportHeight - padding * 2) / contentHeight, 1.2));
  return {
    scale,
    x: viewportWidth / 2 - ((bounds.minX + bounds.maxX) / 2) * scale,
    y: viewportHeight / 2 - ((bounds.minY + bounds.maxY) / 2) * scale
  };
}

export function snapValue(value: number, candidates: number[], threshold: number): { value: number; guide?: number } {
  let bestDistance = threshold + 1;
  let guide: number | undefined;
  for (const candidate of candidates) {
    const distance = Math.abs(value - candidate);
    if (distance < bestDistance && distance <= threshold) {
      bestDistance = distance;
      guide = candidate;
    }
  }
  return guide === undefined ? { value } : { value: guide, guide };
}

export function boundsForRect(item: CanvasRect): Bounds {
  const radians = (item.rotation ?? 0) * Math.PI / 180;
  const cosine = Math.abs(Math.cos(radians));
  const sine = Math.abs(Math.sin(radians));
  const halfWidth = (item.width * cosine + item.height * sine) / 2;
  const halfHeight = (item.width * sine + item.height * cosine) / 2;
  const centerX = item.x + item.width / 2;
  const centerY = item.y + item.height / 2;

  return {
    minX: centerX - halfWidth,
    minY: centerY - halfHeight,
    maxX: centerX + halfWidth,
    maxY: centerY + halfHeight
  };
}

export function boundsForItems(items: CanvasRect[]): Bounds {
  if (!items.length) return { minX: -200, minY: -160, maxX: 200, maxY: 160 };
  return items.reduce<Bounds>((bounds, item) => {
    const itemBounds = boundsForRect(item);
    return {
      minX: Math.min(bounds.minX, itemBounds.minX),
      minY: Math.min(bounds.minY, itemBounds.minY),
      maxX: Math.max(bounds.maxX, itemBounds.maxX),
      maxY: Math.max(bounds.maxY, itemBounds.maxY)
    };
  }, { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });
}

export class ItemSpatialIndex {
  readonly #tree = new RBush<SpatialItem>();

  rebuild(items: SpatialItem[]): void {
    this.#tree.clear();
    this.#tree.load(items);
  }

  visible(bounds: Bounds): string[] {
    return this.#tree.search(bounds).map((item) => item.id);
  }
}
